// Markdown export of a quest (whole) or a single location. The field picker in
// ExportMarkdownModal drives WHICH fields are emitted; this module owns the
// canonical order, the English section titles, and the rendering of each field.
//
// A field is either "inline" (a short line right under the heading, e.g.
// coordinates) or a "section" (its own `### Title` block). `name` is special:
// it is the card heading itself.

// Canonical order + English labels. `default` marks the fields checked on open:
// the authored text of a point in full. Technical metadata (type, paid, tags,
// uuid, id) and the internal comment stay off — one click adds them back.
export const LOCATION_MD_FIELDS = [
  { key: 'name', label: 'Name (heading)', default: true },
  { key: 'coordinates', label: 'Coordinates', default: true },
  { key: 'type', label: 'Type', default: false },
  { key: 'is_paid', label: 'Paid', default: false },
  { key: 'tags', label: 'Tags', default: false },
  { key: 'uuid', label: 'UUID', default: false },
  { key: 'id', label: 'ID', default: false },
  { key: 'comment', label: 'Comment', default: false },
  { key: 'description', label: 'Description', default: true },
  { key: 'task', label: 'Task', default: true },
  { key: 'hint', label: 'Hint', default: true },
  { key: 'answer_variations', label: 'Answer', default: true },
  { key: 'after_message', label: 'After Message', default: true },
  { key: 'branches', label: 'Branching', default: true },
];

// Section titles for the block-style fields.
const SECTION_TITLE = {
  comment: 'Comment',
  description: 'Description',
  task: 'Task',
  hint: 'Hint',
  answer_variations: 'Answer',
  after_message: 'After Message',
  branches: 'Branching',
};

// The default-on set as a plain { key: bool } map for modal initial state.
export const defaultFieldSelection = () =>
  LOCATION_MD_FIELDS.reduce((acc, f) => { acc[f.key] = !!f.default; return acc; }, {});

const isEmpty = (v) => v == null || (typeof v === 'string' && !v.trim()) ||
  (Array.isArray(v) && v.length === 0);

const fmtCoords = (c) => Array.isArray(c) && c.length >= 2
  ? `${Number(c[0]).toFixed(6)}, ${Number(c[1]).toFixed(6)}` : '';

const fmtAnswer = (loc) => {
  if (Array.isArray(loc.answer_variations) && loc.answer_variations.length) {
    return loc.answer_variations.join(' / ');
  }
  if (typeof loc.answer === 'string' && loc.answer.trim()) return loc.answer.trim();
  return '';
};

const fmtBranching = (loc) => {
  const parts = [];
  if (typeof loc.branching_message === 'string' && loc.branching_message.trim()) {
    parts.push(loc.branching_message.trim());
  }
  if (Array.isArray(loc.branches) && loc.branches.length) {
    parts.push('```json', JSON.stringify(loc.branches, null, 2), '```');
  }
  return parts.join('\n\n');
};

// Render one location as Markdown. `fields` is a { key: bool } map; `index` (if
// given) numbers the heading ("## 3. Name").
export function buildLocationMarkdown(loc, fields, index) {
  const on = (k) => !!(fields && fields[k]);
  const out = [];

  // Heading — name is the heading text; number it when an index is supplied.
  const num = Number.isInteger(index) ? `${index + 1}. ` : '';
  const title = on('name') && loc.name ? loc.name : '(no name)';
  out.push(`## ${num}${title}`);

  // Inline lines right under the heading.
  const inline = [];
  if (on('coordinates')) { const c = fmtCoords(loc.coordinates); if (c) inline.push(`📍 ${c}`); }
  const meta = [];
  if (on('type') && !isEmpty(loc.type)) meta.push(`Type: ${loc.type}`);
  if (on('is_paid')) meta.push(`Paid: ${loc.is_paid ? 'yes' : 'no'}`);
  if (on('tags') && Array.isArray(loc.tags) && loc.tags.length) meta.push(`Tags: ${loc.tags.join(', ')}`);
  if (on('uuid') && !isEmpty(loc.uuid)) meta.push(`UUID: \`${loc.uuid}\``);
  if (on('id') && loc.id != null) meta.push(`ID: ${loc.id}`);
  if (meta.length) inline.push(meta.join(' · '));
  if (inline.length) { out.push(''); out.push(inline.join('\n')); }

  // Block sections, in canonical order.
  const section = (title, body) => {
    if (isEmpty(body)) return;
    out.push('', `### ${title}`, String(body).trim());
  };
  if (on('comment')) section(SECTION_TITLE.comment, loc.comment);
  if (on('description')) section(SECTION_TITLE.description, loc.description);
  if (on('task')) section(SECTION_TITLE.task, loc.task);
  if (on('hint')) section(SECTION_TITLE.hint, loc.hint);
  if (on('answer_variations')) section(SECTION_TITLE.answer_variations, fmtAnswer(loc));
  if (on('after_message')) section(SECTION_TITLE.after_message, loc.after_message);
  if (on('branches')) section(SECTION_TITLE.branches, fmtBranching(loc));

  return out.join('\n');
}

// Render the whole quest: a `# quest_name` header (+ description if selected),
// then every location. `questFields` optionally toggles the quest-level header
// pieces; pass { name, description } booleans.
export function buildQuestMarkdown(questInfo, locations, fields, questFields = {}) {
  const out = [];
  const qName = (questInfo && (questInfo.quest_name || questInfo.name)) || 'Quest';
  if (questFields.name !== false) out.push(`# ${qName}`);
  if (questFields.description && questInfo && questInfo.quest_description) {
    out.push('', String(questInfo.quest_description).trim());
  }
  (locations || []).forEach((loc, i) => {
    out.push('', '---', '', buildLocationMarkdown(loc, fields, i));
  });
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim() + '\n';
}
